import Tippy from '@tippyjs/react'
import { AnimatePresence, motion, useDragControls, useMotionValue } from 'framer-motion'
import React, { useRef, useState } from 'react'
import { useClickAway, useKeyPressEvent, useMeasure, useMedia, useWindowSize } from 'react-use'
import styled, { css } from 'styled-components'
import { phoneMaxWidth } from 'utils/media'
import { Portal } from './Portal'

const SHEET_HEIGHT = '100vh'

const StyledTippy = styled(Tippy)<{ $round?: boolean }>`
  background-color: white;
  border-radius: 8px;
  border: 1px solid var(--black-15);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);

  // Tippy uses inline styles for max-width...
  max-width: 390px !important;

  .tippy-content {
    padding: 0;
  }

  .tippy-arrow:before {
    color: white;
  }

  ${(props) =>
    props.$round &&
    css`
      border-radius: 40px;
    `}
`

const Sheet = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  left: 0;
  bottom: -100vh;
  z-index: 100;
  height: auto;
  width: 100%;
  padding: 0px 24px calc(${SHEET_HEIGHT} + 24px) 24px;
  background-color: white;
  border-radius: 20px 20px 0px 0px;
  touch-action: none;
`

export const Background = styled(motion.div)`
  background-color: black;
  width: 100%;
  height: 100vh;
  opacity: 0.3;
  position: fixed;
  z-index: 50;
  top: 0;
  left: 0;
`

const HandleContainer = styled.div`
  padding: 24px;
  padding-top: 8px;
  cursor: grab;
`
const Handle = styled.div`
  height: 5px;
  width: 48px;
  background-color: var(--black-20);
  border-radius: 10px;
`

const Wrapper = styled.div`
  display: inline-flex;
`

export const useTippyPopoverController = () => {
  const [open, setOpen] = useState<boolean>(false)

  return {
    open,
    setOpen
  }
}

interface Props {
  content: React.ReactNode
  children: React.ReactElement
  controls: ReturnType<typeof useTippyPopoverController>
  placement?: Parameters<typeof Tippy>[0]['placement']
  round?: boolean
  arrow?: boolean
  defaultTriggerOnDesktop?: boolean
  delay?: Parameters<typeof Tippy>[0]['delay']
}

export const Tooltip = ({ content, children, controls, placement = 'bottom', round, arrow, defaultTriggerOnDesktop = false, delay }: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { width: windowWidth } = useWindowSize()

  useClickAway(ref, () => {
    if (controls.open && phoneMaxWidth < windowWidth) {
      controls.setOpen(false)
    }
  })

  useKeyPressEvent('Escape', () => {
    if (controls.open) {
      controls.setOpen(false)
    }
  })

  return (
    <Wrapper ref={ref}>
      <StyledTippy
        arrow={arrow}
        placement={placement}
        content={content}
        visible={defaultTriggerOnDesktop ? undefined : controls.open}
        delay={delay}
        interactive
        $round={round}>
        {children}
      </StyledTippy>
    </Wrapper>
  )
}

export const BottomSheet = ({ controls, content }: Omit<Props, 'children'>) => {
  const dragControls = useDragControls()
  const sheetVerticalPosition = useMotionValue(0)
  const [ref, { height }] = useMeasure()

  const maxDragDown = height / 2

  if (!content) {
    return null
  }

  return (
    <AnimatePresence>
      {controls.open && (
        <Portal>
          <Background onClick={() => controls.setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} exit={{ opacity: 0 }} />
          <Sheet
            ref={ref}
            style={{ y: sheetVerticalPosition }}
            initial={{ opacity: 0, translateY: height }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: height }}
            transition={{ translateY: { duration: 0.2, ease: 'easeOut' }, opacity: { duration: 0.2 } }}
            drag="y"
            dragElastic={0.2}
            dragDirectionLock
            dragSnapToOrigin
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: height }}
            onDragEnd={() => {
              if (sheetVerticalPosition.get() > maxDragDown) {
                controls.setOpen(false)
              }
            }}>
            <HandleContainer
              onPointerDown={(event) => {
                dragControls.start(event)
              }}>
              <Handle />
            </HandleContainer>

            {content}
          </Sheet>
        </Portal>
      )}
    </AnimatePresence>
  )
}

export const TippyPopover = ({ content, children, controls, ...tippyProps }: Props) => {
  const isMobile = useMedia(`(max-width: ${phoneMaxWidth}px)`)

  if (isMobile) {
    return (
      <>
        {children}
        {content && <BottomSheet controls={controls} content={content} />}
      </>
    )
  } else {
    return (
      <Tooltip {...tippyProps} controls={controls} content={content}>
        {children}
      </Tooltip>
    )
  }
}
